// Client-side evaluation of a SearchRq, for tables small enough that the
// server does not paginate them.
//
// This is knowingly a second implementation of semantics the Rust translator
// owns, and the risk is drift. It is contained deliberately: only the
// operators these tables offer, tested as the reference the server is written
// against, and covering one table (templates) that has no server-side
// counterpart. If templates ever moves server-side, delete this rather than
// maintain it.
//
// The null handling below is the subtle part, and it is not SQL's. In SQL
// `lower(col) LIKE '%x%'` on a NULL column yields NULL, which excludes the
// row — and so does `NOT (NULL)`, so plain SQL drops a NULL row from both
// Contains and NotContains. The server deliberately overrides that: its
// NotContains is written `(col IS NULL OR NOT (... LIKE ...))`, because a
// missing value plainly does not contain the search term and "every location not
// yet on 0.2.0" means to include the ones reporting no version at all.
//
// So the rule this file implements, matching the server: Contains on a null
// is false, NotContains on a null is true.

import type { SearchRq, TextOp } from "@/api/types";

export function matchesText(value: string | null, op: TextOp): boolean {
    // A missing value does not contain the term, so it fails Contains and
    // satisfies NotContains. This mirrors the server's deliberate override of
    // SQL's three-valued logic — see the translator's `text` method.
    if (value === null) return op.type === "NotContains";
    const haystack = value.toLowerCase();
    const needle = op.value.toLowerCase();
    const contains = haystack.includes(needle);
    return op.type === "Contains" ? contains : !contains;
}

// Only `query` is read. There used to be a `filters` argument too, which
// nothing looked at and every caller passed empty — the tables this serves
// offer free text and no facets. Still `Pick`ed from `SearchRq` rather than
// spelled out, so a call site keeps the shape of a real request and has
// nothing to translate if its table ever moves server-side.
export function filterRows<T>(
    rows: T[],
    rq: Pick<SearchRq<unknown, string, string>, "query">,
    searchFields: (row: T) => (string | null)[],
): T[] {
    const query = rq.query?.value?.trim();
    if (!query) return rows;
    return rows.filter((row) =>
        searchFields(row).some((field) => matchesText(field, { type: "Contains", value: query })),
    );
}
